/* ==========================================================================
   DealCraft · cart guard

   Three rules, enforced in one place, after every cart change:

   1. A digital file is never in the cart more than once. A line above 1 is
      put back to 1. Nobody should pay 998 for one PDF.
   2. The Box and the Codex are never in the cart together. The Box already
      contains the Codex, so when both are present the Codex line goes.
   3. Adding a file the cart already holds, or adding the Codex while the Box
      is in the cart, is stopped before the request leaves the page, with a
      message next to the button the buyer just pressed. Rules 1 and 2 still
      run afterwards, so anything that slips past (another tab, a stale page,
      JavaScript arriving late) is corrected on the next pass.

   It runs on load, on bfcache restore, after every change Ride publishes
   (PUB_SUB_EVENTS.cartUpdate: add to cart, remove, quantity), and after the
   Codex -> Box swap. Only the two file products are touched, by variant id
   read from <html data-dc-single>. Lines it does not own are never written.

   Every write is one POST to /cart/update.js carrying all corrections at
   once. The response status is checked; a failed write is left alone and
   never retried in a loop. On the cart page a corrected cart is re-rendered
   by a single reload, guarded so a persistent failure cannot reload forever.
   Elsewhere the header bubble and the notification's "view cart" button are
   re-fetched through the Section Rendering API so the count is right.
   ========================================================================== */
(function () {
  'use strict';

  var html = document.documentElement;
  var ids = (html.getAttribute('data-dc-single') || '')
    .split(',')
    .map(function (x) { return parseInt(x, 10); })
    .filter(Boolean);
  if (!ids.length) return;

  var CODEX = ids[0];
  var BOX = ids[1] || null;

  var MSG = {
    already: 'الملف ده في السلة بالفعل. ملف واحد يكفي.',
    boxHasIt: 'الـ Box في سلتك بالفعل، والـ Revenue Codex جوّاه. مش محتاج تضيفه تاني.',
    removedCodex: 'الـ Box في سلتك، والـ Revenue Codex جوّاه. اتشال الـ Codex من السلة عشان ما تدفعش تمنه مرتين.',
    clamped: 'الملف كان متضاف أكتر من مرة. اتظبط لملف واحد.',
    viewCart: 'بنوديك للدفع...',
  };

  var RELOAD_KEY = 'dc-guard-reloaded';
  var busy = false;
  var snapshot = null; // last cart read, used by the pre-submit check

  function root() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return r || '/';
  }

  function has(cart, variantId) {
    return !!variantId && (cart.items || []).some(function (i) { return i.variant_id === variantId; });
  }

  function readCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { if (!r.ok) throw new Error('cart read'); return r.json(); })
      .then(function (cart) { snapshot = cart; return cart; });
  }

  // What has to change, or null when the cart is already right.
  function plan(cart) {
    var updates = {};
    var reasons = {};
    var count = 0;
    var box = has(cart, BOX);
    (cart.items || []).forEach(function (line) {
      if (ids.indexOf(line.variant_id) === -1) return;
      if (box && line.variant_id === CODEX) {
        updates[line.key] = 0; reasons.removedCodex = true; count++;
      } else if (line.quantity > 1) {
        updates[line.key] = 1; reasons.clamped = true; count++;
      }
    });
    return count ? { updates: updates, reasons: reasons } : null;
  }

  function write(updates) {
    return fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) { if (!r.ok) throw new Error('cart write'); return r.json(); });
  }

  function onCartPage() {
    var here = window.location.pathname.replace(/\/+$/, '');
    var cart = (root() + 'cart').replace(/\/+$/, '');
    return here === cart;
  }

  // Re-fetch small pieces of chrome that show the cart count.
  function refreshChrome() {
    var wanted = ['cart-icon-bubble', 'cart-notification-button'].filter(function (id) {
      return document.getElementById(id);
    });
    if (!wanted.length) return Promise.resolve();
    return fetch(root() + '?sections=' + wanted.join(','), { cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : {}; })
      .then(function (sections) {
        wanted.forEach(function (id) {
          if (!sections[id]) return;
          var doc = new DOMParser().parseFromString(sections[id], 'text/html');
          var inner = doc.querySelector('.shopify-section');
          if (inner) document.getElementById(id).innerHTML = inner.innerHTML;
        });
      })
      .catch(function () {});
  }

  // Sections that read the cart in Liquid and are not part of Ride's own
  // re-render (the cart value panel). Each carries its section id.
  function refreshLiveSections() {
    var nodes = document.querySelectorAll('[data-dc-live-section]');
    if (!nodes.length) return Promise.resolve();
    return Promise.all(Array.prototype.map.call(nodes, function (node) {
      var id = node.getAttribute('data-dc-live-section');
      var wrapper = document.getElementById('shopify-section-' + id);
      if (!wrapper) return null;
      return fetch(root() + 'cart?section_id=' + encodeURIComponent(id), { cache: 'no-store' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (text) {
          if (!text) return;
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var fresh = doc.getElementById('shopify-section-' + id) || doc.body;
          wrapper.innerHTML = fresh.innerHTML;
        })
        .catch(function () {});
    }));
  }

  function noteInNotification(reasons) {
    var host = document.getElementById('cart-notification-product');
    if (!host) return;
    var text = reasons.removedCodex ? MSG.removedCodex : MSG.clamped;
    var old = host.querySelector('.dc-guard-note');
    if (old) old.remove();
    var p = document.createElement('p');
    p.className = 'dc-guard-note';
    p.setAttribute('role', 'status');
    p.textContent = text;
    host.appendChild(p);
  }

  function guard() {
    if (busy) return Promise.resolve();
    busy = true;
    return readCart()
      .then(function (cart) {
        var p = plan(cart);
        if (!p) {
          try { sessionStorage.removeItem(RELOAD_KEY); } catch (e) {}
          return null;
        }
        return write(p.updates).then(function (updated) {
          snapshot = updated;
          return { cart: updated, reasons: p.reasons, settled: !plan(updated) };
        });
      })
      .then(function (result) {
        busy = false;
        if (!result) return;
        document.dispatchEvent(new CustomEvent('dc:cart:guarded', { bubbles: true, detail: result }));
        if (onCartPage()) {
          var done = false;
          try { done = sessionStorage.getItem(RELOAD_KEY) === '1'; } catch (e) {}
          if (result.settled && !done) {
            try { sessionStorage.setItem(RELOAD_KEY, '1'); } catch (e) {}
            window.location.reload();
            return;
          }
          // Either already reloaded once or the write did not settle: refresh
          // in place instead of looping.
          return refreshLiveSections();
        }
        noteInNotification(result.reasons);
        return refreshChrome();
      })
      .catch(function () { busy = false; });
  }

  // ---- Stop the add before it leaves the page ---------------------------
  function showNote(form, text) {
    var old = form.querySelector('.dc-guard-note');
    if (old) old.remove();
    var p = document.createElement('p');
    p.className = 'dc-guard-note';
    p.setAttribute('role', 'status');
    p.setAttribute('aria-live', 'polite');
    p.appendChild(document.createTextNode(text + ' '));
    var a = document.createElement('a');
    a.href = root() + 'cart';
    a.textContent = MSG.viewCart;
    p.appendChild(a);
    var btn = form.querySelector('[type="submit"]');
    if (btn && btn.parentNode) btn.parentNode.insertBefore(p, btn.nextSibling);
    else form.appendChild(p);
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.getAttribute) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    var idInput = form.querySelector('[name="id"]');
    var variantId = idInput ? parseInt(idInput.value, 10) : NaN;
    if (ids.indexOf(variantId) === -1) return;
    if (!snapshot) return; // no snapshot yet; the pass after the add corrects it
    var text = null;
    if (has(snapshot, variantId)) text = MSG.already;
    else if (variantId === CODEX && has(snapshot, BOX)) text = MSG.boxHasIt;
    if (!text) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showNote(form, text);
    // The cart already holds what the buyer is trying to buy (the file, or
    // the Box that contains it). Nothing to add, so go straight to payment.
    window.setTimeout(function () { window.location.href = root() + 'checkout'; }, 900);
  }, true);

  // ---- Triggers ---------------------------------------------------------
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', guard);
  else guard();
  window.addEventListener('pageshow', function (e) { if (e.persisted) guard(); });
  document.addEventListener('dc:upgrade:complete', function () { setTimeout(guard, 400); });
  document.addEventListener('cart:refresh', guard);
  // PUB_SUB_EVENTS is a top-level const in constants.js, so it is not a
  // window property; test the binding itself.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      // On the cart page every change also re-renders the Liquid panels that
      // read the cart (the value panel), even when nothing needed correcting:
      // a plain Remove must not leave them describing a line that is gone.
      return guard().then(function () {
        if (onCartPage()) return refreshLiveSections();
      });
    });
  }
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartError) {
    subscribe(PUB_SUB_EVENTS.cartError, function () { readCart().catch(function () {}); });
  }
})();
